import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Modal } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import axiosInstance from '../../utils/axiosInstanse';
import { motion } from 'framer-motion';
import Button from '../CustomButton/Button';
import host from "../../host";
import { format, getYear } from 'date-fns';
import { ru } from 'date-fns/locale';
import './pool.css';
import { showNotification } from '../../utils/notificationUtils';
import { useNavigate } from 'react-router-dom';
import TIME_SLOTS_MAP from './time_slots';

const getWeekdayName = (fdayw) => {
    const weekdays = {
        1: 'понедельник',
        2: 'вторник',
        3: 'среда',
        4: 'четверг',
        5: 'пятница',
        6: 'суббота',
        7: 'воскресенье'
    };
    return weekdays[fdayw] || 'неизвестный день';
};

const getMonthName = (monthNumber) => {
    const months = {
        '01': 'январь',
        '02': 'февраль',
        '03': 'март',
        '04': 'апрель',
        '05': 'май',
        '06': 'июнь',
        '07': 'июль',
        '08': 'август',
        '09': 'сентябрь',
        '10': 'октябрь',
        '11': 'ноябрь',
        '12': 'декабрь'
    };
    return months[monthNumber] || 'неизвестный месяц';
};

const getDatesForWeekdayInMonth = (fdayw, monthNumber) => {
    const currentYear = getYear(new Date());
    const year = currentYear;
    const month = parseInt(monthNumber) - 1; // monthNumber 1-12, JS month 0-11

    const dates = [];
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const targetDay = fdayw === 7 ? 0 : fdayw;

    for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
        if (d.getDay() === targetDay) {
            dates.push(new Date(d));
        }
    }

    return dates;
};

function PoolActive() {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cancellingId, setCancellingId] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        fetchBookings();
    }, []);

    const fetchBookings = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`${host}api/my_bookings/`, {});

            if (Array.isArray(response.data) && response.data.length > 0) {
                setBookings(response.data);
            } else if (response.data && !Array.isArray(response.data)) {
                setBookings([response.data]);
            } else {
                setBookings([]);
            }
        } catch (error) {
            setBookings([]);
        } finally {
            setLoading(false);
        }
    };

    // Функция для открытия модалки
    const openCancelModal = (booking) => {
        setSelectedBooking(booking);
        setShowModal(true);
    };

    // Функция для закрытия модалки
    const closeCancelModal = () => {
        setShowModal(false);
        setSelectedBooking(null);
    };

    // Функция для подтверждения отмены
    const confirmCancelBooking = async () => {
        if (!selectedBooking || cancellingId === selectedBooking.ID) {
            closeCancelModal();
            return;
        }

        setCancellingId(selectedBooking.ID || selectedBooking.id);
        closeCancelModal();

        try {
            const currentYear = getYear(new Date());
            const period = `${currentYear}${selectedBooking.month}`;

            const response = await axiosInstance.delete(`${host}api/booking_cancel/`, {
                data: {
                    booking_id: selectedBooking.ID || selectedBooking.id,
                    period: period
                },
            });

            if (response.status === 200) {
                setBookings(prevBookings =>
                    prevBookings.filter(b => (b.ID || b.id) !== (selectedBooking.ID || selectedBooking.id))
                );

                showNotification(dispatch, `Абонемент на ${getMonthName(selectedBooking.month)} успешно отменен`, 'success');
            }
        } catch (error) {
            if (error.response?.status === 423) {
                const errorMessage = 'Транзакция заблокирована администратором, попробуйте позже';
                showNotification(dispatch, errorMessage, 'error');
            } else {
                const errorMessage = error.response?.data?.ERROR || 'Ошибка при отмене абонемента';
                showNotification(dispatch, errorMessage, 'error');
            }
        } finally {
            setCancellingId(null);
            setSelectedBooking(null);
        }
    };

    const renderBookingCard = (booking) => {
        const timeSlot = TIME_SLOTS_MAP[booking.STIME] || `Режим ${booking.STIME}`;
        const weekday = getWeekdayName(booking.FDAYW);
        const monthName = getMonthName(booking.month);
        const bookingDates = getDatesForWeekdayInMonth(booking.FDAYW, booking.month);
        const isCancelling = cancellingId === (booking.ID || booking.id);

        return (
            <motion.div
                key={booking.ID || booking.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
            >
                <Card className="booking-card active shadow mb-4">
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-start mb-3">
                            <div>
                                <Card.Title className="mb-2">
                                    Абонемент в бассейн
                                </Card.Title>
                                <Card.Subtitle className="text-muted mb-3">
                                    {weekday}, {timeSlot}
                                </Card.Subtitle>
                            </div>
                            <span className="badge bg-success px-3 py-2">
                                Активно
                            </span>
                        </div>

                        <div className="booking-details mb-4">
                            <div className="mb-2">
                                <strong>Номер брони:</strong> #{booking.ID || booking.id}
                            </div>
                            <div className="mb-2">
                                <strong>Табельный номер:</strong> {booking.PERNR}
                            </div>
                            <div className="mb-2">
                                <strong>Месяц абонемента:</strong> {monthName}
                            </div>
                            <div className="mb-2">
                                <strong>Количество занятий:</strong> {bookingDates.length > 4 ? "4" : bookingDates.length}
                            </div>

                            <div className="mt-3">
                                <strong>Даты занятий в {monthName}:</strong>
                                <div className="mt-2">
                                    {bookingDates.length > 0 ? (
                                        bookingDates.map((date, idx) => (
                                            <div key={idx} className="text-muted small py-1">
                                                {format(date, 'dd MMMM yyyy', { locale: ru })}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-muted small py-1">Нет дат в этом месяце</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="d-grid">
                            <Button
                                onClick={() => openCancelModal(booking)}
                                disabled={isCancelling}
                                style={{
                                    backgroundColor: '#dc3545',
                                    borderColor: '#dc3545',
                                    padding: '12px',
                                    fontSize: '1rem'
                                }}
                            >
                                {isCancelling ? (
                                    <>
                                        <Spinner
                                            animation="border"
                                            size="sm"
                                            className="me-2"
                                        />
                                        Отменяем...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-x-circle me-2"></i>
                                        Отменить
                                    </>
                                )}
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            </motion.div>
        );
    };

    const renderEmptyState = () => (
        <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
        >
            <Alert variant="info" className="text-center py-5">
                <div className="mb-4">
                    <i className="bi bi-calendar-x" style={{ fontSize: '4rem', color: '#0dcaf0' }}></i>
                </div>
                <Alert.Heading className="mb-3">У вас нет активных абонементов</Alert.Heading>
                <p className="mb-4">
                    Вы можете приобрести абонемент на следующий месяц.
                    Абонемент дает право посещать занятия в определенный день недели в течение всего месяца.
                </p>
                <Button onClick={() => navigate('/note/pool_booking')}>
                    Бронь
                </Button>
            </Alert>
        </motion.div>
    );

    if (loading) {
        return (
            <Container fluid className="pool-container">
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">Загрузка информации об абонементах...</p>
                </div>
            </Container>
        );
    }

    return (
        <>
            <Container fluid className="pool-container">
                <Row>
                    <Col md={8} lg={6} className="mx-auto">
                        {bookings.length > 0 ? (
                            bookings.map(booking => renderBookingCard(booking))
                        ) : (
                            renderEmptyState()
                        )}
                    </Col>
                </Row>
            </Container>

            {/* Модальное окно подтверждения */}
            <Modal show={showModal} onHide={closeCancelModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Подтверждение отмены</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedBooking && (
                        <>
                            <p>Вы уверены, что хотите отменить абонемент?</p>
                            <div className="mt-3 p-3 bg-light rounded">
                                <div><strong>Месяц:</strong> {getMonthName(selectedBooking.month)}</div>
                                <div><strong>День недели:</strong> {getWeekdayName(selectedBooking.FDAYW)}</div>
                                <div><strong>Время:</strong> {TIME_SLOTS_MAP[selectedBooking.STIME] || `Режим ${selectedBooking.STIME}`}</div>
                                <div><strong>Номер брони:</strong> #{selectedBooking.ID || selectedBooking.id}</div>
                            </div>
                            <p className="mt-3 text-danger">
                                Это действие нельзя отменить. Абонемент будет полностью аннулирован.
                            </p>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        onClick={closeCancelModal}
                        style={{
                            backgroundColor: '#6c757d',
                            borderColor: '#6c757d',
                            padding: '8px 16px'
                        }}
                    >
                        Отмена
                    </Button>
                    <Button
                        onClick={confirmCancelBooking}
                        style={{
                            backgroundColor: '#dc3545',
                            borderColor: '#dc3545',
                            padding: '8px 16px'
                        }}
                    >
                        Да, отменить
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default PoolActive;